import { Injectable } from "@angular/core";
import { AppConstants } from "@core/constants";
import { ApiBaseService } from "@core/services/api-base.service";
import { SportDto } from "@core/services/sports/sport-dto";
import { IMaximumAerobicSpeedPowerSetting, IMaximumAerobicSpeedPowerSettingsWrapper } from "@shared/interfaces/metric-settings.interface";
import { BehaviorSubject, delay, distinctUntilChanged, finalize, forkJoin } from "rxjs";

export interface StateStatus {
  status: 'ok' | 'warning' | 'error';
  message: string
}

interface SportResponse {
  results: SportDto[]
}

@Injectable()
export class MaximumAerobicPanelComponentState {
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _saving = new BehaviorSubject<boolean>(false);
  private readonly _settings = new BehaviorSubject<IMaximumAerobicSpeedPowerSetting[] | null>(null);
  private readonly _status = new BehaviorSubject<StateStatus | null>(null);
  private readonly _sports = new BehaviorSubject<SportDto[]>([]);

  public loading$ = this._loading.asObservable().pipe(delay(0), distinctUntilChanged());
  public saving$ = this._saving.asObservable().pipe(delay(0), distinctUntilChanged());
  public settings$ = this._settings.asObservable();
  public sports$ = this._sports.asObservable();
  public status$ = this._status.asObservable().pipe(delay(0), distinctUntilChanged());

  constructor(
    private _apiService: ApiBaseService
  ) { }

  public loadState(featureId: number): void {
    this._loading.next(true);

    const settingsUrl = `${AppConstants.API.FEATURE_ITEM.GET}${featureId}/settings/`;
    const sportsUrl = AppConstants.API.SPORT.LIST;

    forkJoin({
      settings: this._apiService.getJson<IMaximumAerobicSpeedPowerSettingsWrapper>(settingsUrl),
      sports: this._apiService.getJson<SportResponse>(sportsUrl)
    }).pipe(
      finalize(() => {
        this._loading.next(false);
      })
    ).subscribe({
      next: ({settings, sports}) => {
        this._settings.next(settings.settings);
        this._sports.next(sports.results);
      },
      error: (err) => {
        this._status.next({
          status: 'error',
          message: 'Unable to load settings for Max Aerobic Speed/Power.'
        });
        console.error(err);
      }
    })
  }

  public saveSettings(featureId: number, settings: Record<string, any>): void {
    this._saving.next(true);

    const url = `${AppConstants.API.FEATURE_ITEM.GET}${featureId}/settings/`;
    this._apiService.postJson<IMaximumAerobicSpeedPowerSettingsWrapper>(url, { settings })
      .pipe(
        finalize(() => {
          this._saving.next(false);
        })
      ).subscribe({
        next: (_response: IMaximumAerobicSpeedPowerSettingsWrapper) => {
          this._status.next({
            status: 'ok',
            message: 'Successfully saved settings for Max Aerobic Speed/Power'
          });
        },
        error: (err) => {
          this._status.next({
            status: 'error',
            message: 'Unable to save settings for Max Aerobic Speed/Power'
          });
          console.error(err);
        }
      })
  }
}